<template>
  <div class="auth-page">
    <!-- Header -->
    <header class="auth-header auth-shell">
      <a href="https://sacktechlogistics.com/" class="auth-logo">
        <img src="@/assets/images/sack/logo.png" alt="Sack Tech Logistics" />
      </a>
      <div class="auth-header-right">
        <a href="https://sacktechlogistics.com/#contacto" class="auth-help">
          <i class="bi bi-headset"></i><span>¿Necesitas ayuda?</span>
        </a>
        <span class="auth-divider"></span>
        <span class="auth-locale">ES / México</span>
      </div>
    </header>

    <!-- Contenido principal -->
    <main class="auth-main auth-shell">
      <div class="auth-stage">
        <section class="auth-copy auth-copy-left">
          <span class="auth-eyebrow">INNOVACIÓN Y<br />AUTOMATIZACIÓN</span>
          <h2 class="auth-copy-title">Todos tus<br />envíos.<br />Un solo lugar.</h2>
          <p class="auth-copy-text">Tecnología que conecta tu<br />negocio con su próximo<br />destino.</p>
        </section>

        <img src="@/assets/images/sack/decor-left.png" class="auth-decor auth-decor-left" alt="" />

        <div class="auth-card-wrap">
        <div class="auth-card" :class="{ 'auth-card--shake': shake }" @animationend="onCardAnimationEnd">
          <h1 class="auth-card-title">Iniciar sesión</h1>
          <p class="auth-card-subtitle">Ingresa a tu cuenta y continúa<br />moviendo tu negocio.</p>

          <!-- Alerts de usuario -->
          <div v-if="newUser && isApproved" class="alert alert-success auth-alert">
            Su cuenta ha sido creada exitosamente.
          </div>
          <div v-if="newUser && !isApproved" class="alert alert-warning auth-alert">
            Su cuenta aún no ha sido aprobada. Será notificado vía WhatsApp.
          </div>

          <form @submit.prevent="signin" novalidate>
            <label for="email" class="auth-label">Correo electrónico *</label>
            <input type="email" id="email" class="auth-input" v-model.trim="email" @input="$v.email.$touch()"
              :class="{ 'is-invalid': $v.email.$error }" placeholder="nombre@empresa.com" required>
            <div v-if="$v.email.$error" class="auth-feedback">Debes introducir un correo electrónico válido</div>

            <label for="password" class="auth-label auth-label-password">Contraseña *</label>
            <div class="auth-password">
              <input :type="showPassword ? 'text' : 'password'" id="password" class="auth-input"
                v-model.trim="password" @input="$v.password.$touch()" :class="{ 'is-invalid': $v.password.$error }"
                placeholder="Ingresa tu contraseña" required>
              <button type="button" class="auth-password-toggle" tabindex="-1"
                :aria-label="showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'"
                @click="showPassword = !showPassword">
                <i :class="showPassword ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
              </button>
            </div>
            <div v-if="$v.password.$error" class="auth-feedback">
              La contraseña debe tener entre {{ $v.password.$params.minLength.min }} y {{
                $v.password.$params.maxLength.max }} caracteres
            </div>

            <div v-if="signinError" class="alert alert-danger auth-alert auth-alert-error">
              Correo y/o contraseña inválidos
            </div>

            <div class="auth-row">
              <label class="auth-remember">
                <input type="checkbox" id="rememberMe" v-model="checked" />
                <span>Mantener sesión<br />iniciada</span>
              </label>
              <router-link to="/password-recovery" class="auth-forgot">¿Olvidaste tu contraseña?</router-link>
            </div>

            <button type="submit" class="auth-submit" :disabled="$v.$invalid || isLoading" :aria-busy="isLoading">
              <span v-if="isLoading" class="auth-spinner" aria-hidden="true"></span>
              <span v-if="isLoading">Ingresando...</span>
              <template v-else><span>Ingresar</span><i class="bi bi-arrow-right"></i></template>
            </button>
          </form>

          <p class="auth-signup">
            <span>¿Aún no tienes una cuenta?</span>
            <router-link to="/signup" class="auth-signup-link">Crea tu cuenta</router-link>
          </p>
        </div>
        </div>

        <img src="@/assets/images/sack/decor-right.png" class="auth-decor auth-decor-right" alt="" />

        <section class="auth-copy auth-copy-right">
          <span class="auth-eyebrow">CONECTADOS PARA<br />CRECER</span>
          <h2 class="auth-copy-title auth-copy-title-sm">El siguiente<br />paso<br />empieza aquí.</h2>
          <ul class="auth-checklist">
            <li><i class="bi bi-check-lg"></i>Tu tienda en línea<br />conectada</li>
            <li><i class="bi bi-check-lg"></i>¡Guías y seguimiento en<br />un lugar!</li>
            <li><i class="bi bi-check-lg"></i>Soporte local para<br />tu operación</li>
          </ul>
        </section>
      </div>
    </main>

    <!-- Footer -->
    <footer class="auth-footer auth-shell">
      <span>© {{ currentYear }} Sack Tech Logistics. Todos los derechos reservados.</span>
      <div class="auth-footer-links">
        <a href="https://sacktechlogistics.com/aviso-de-privacidad/" target="_blank" rel="noopener">Aviso de privacidad</a>
        <a href="https://sacktechlogistics.com/terminos-y-condiciones/" target="_blank" rel="noopener">Términos de uso</a>
      </div>
    </footer>
  </div>
</template>

<script>
import axios from 'axios'
import commons from '../commons'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import {
  required,
  minLength,
  maxLength,
  email
} from 'vuelidate/lib/validators'

export default {
  name: 'SignIn',
  data() {
    return {
      email: '',
      password: '',
      signinError: false,
      newUser: false,
      isLoading: false,
      checked: false,
      showPassword: false,
      shake: false
    }
  },
  created() {
    // Si hay un usuario en localStorage pero no existe la bandera temporal,
    // significa que no pidió mantener sesión → lo borramos al recargar
    const hasSessionFlag = sessionStorage.getItem('session-active');
    const userId = localStorage.getItem('ew-user-id');

    if (userId && !hasSessionFlag) {
      localStorage.removeItem('ew-user-id');
      localStorage.removeItem('ew-user-token');
    }

    if (localStorage.getItem('ew-user-id')) {
      this.$router.push('/shipments')
      console.log('you are logged in')
    }

    this.newUser = this.$route.query.newUser
  },
  methods: {
    triggerShake() {
      // Se reinicia la clase para que la sacudida corra en cada intento fallido
      this.shake = false
      this.$nextTick(() => {
        this.shake = true
      })
    },
    onCardAnimationEnd(event) {
      if (event.animationName.indexOf('auth-shake') === 0) {
        this.shake = false
      }
    },
    signin() {
      this.isLoading = true
      this.signinError = false
      this.newUser = false
      axios
        .post(`${commons.apiURL}/api/v1/login`, {
          email: this.email,
          password: this.password
        })
        .then(response => {
          // Guardamos siempre en localStorage para compatibilidad
          localStorage.setItem('ew-user-id', response.data.id)
          localStorage.setItem('ew-user-token', response.data.token)
          localStorage.setItem('ew-user-role', response.data.rol)

          if (this.checked) {
            // Mantener sesión → guardamos una bandera permanente
            localStorage.setItem('keep-session', 'true')
            sessionStorage.setItem('session-active', 'true') // también para la sesión actual
          } else {
            // No mantener sesión → solo guardamos la bandera temporal
            sessionStorage.setItem('session-active', 'true')
            localStorage.removeItem('keep-session')
          }

          this.$router.push('/dashboard')
        })
        .catch(error => {
          console.log(error.response)
          if (error.response.status === 404) {
            this.signinError = true
            if (error.response.data.message == 'no active') {
              this.signinError = false
              this.newUser = true
            } else {
              this.triggerShake()
            }
          }
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    ...mapActions(['quoting', 'newShipment', 'getAllShipments']),
    ...mapMutations(['setNewShipmentRequestBody'])
  },
  computed: {
    ...mapGetters(['newShipmentRequestBody']),
    currentYear() {
      return new Date().getFullYear()
    }
  },
  validations: {
    email: {
      required,
      email,
      maxLength: maxLength(100)
    },
    password: {
      required,
      minLength: minLength(4),
      maxLength: maxLength(12)
    }
  }
}
</script>

<style scoped>
/* Medidas del diseño (1920x1080): espaciados/elementos al 63% y textos al 90% */
.auth-page {
  --navy: #031d7d;
  --navy-button: #011b6f;
  --magenta: #a80b5f;
  --text-muted: #808080;
  --text-label: #393939;
  --text-check: #6471ad;
  --panel: #f8faff;
  --line: #ebeff7;
  --input-border: #8f95a5;

  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #fff;
  font-family: 'Inter', -apple-system, 'Segoe UI', Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
}

.auth-shell {
  margin-left: 48.1px;
  margin-right: 52.6px;
}

/* Header */
.auth-header {
  height: 95.1px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 56.6px 9.5px 53.7px;
}

.auth-logo {
  display: block;
  width: 211px;
  height: 75px;
  overflow: hidden;
}

/* logo.png trae mucho margen en blanco; se recorta con CSS sin alterar el archivo */
.auth-logo img {
  display: block;
  height: 146.6px;
  width: auto;
  max-width: none;
  margin: -38.5px 0 0 -6.8px;
}

.auth-header-right {
  display: flex;
  align-items: center;
  padding-top: 2.5px;
}

.auth-help {
  display: inline-flex;
  align-items: center;
  gap: 6.3px;
  color: var(--navy);
  font-size: 14.7px;
  font-weight: 500;
  text-decoration: none;
}

.auth-help i {
  font-size: 13.2px;
  line-height: 1;
}

.auth-help:hover {
  color: var(--magenta);
  text-decoration: none;
}

.auth-divider {
  width: 1px;
  height: 11.8px;
  background: #d1d4da;
  margin: 0 23.5px 0 37.8px;
}

.auth-locale {
  color: var(--text-muted);
  font-size: 13.5px;
}

/* Panel central */
.auth-main {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--panel);
  border-top: 1px solid var(--line);
  border-bottom: 1px solid var(--line);
}

.auth-stage {
  width: 100%;
  padding: 18px 1.3px 18px 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Textos laterales */
.auth-copy {
  width: 180px;
  flex-shrink: 0;
  white-space: nowrap;
}

.auth-copy-left {
  margin-right: 7.1px;
  transform: translateY(-9px);
}

.auth-copy-right {
  margin-left: 14.5px;
  transform: translateY(-2.2px);
}

.auth-eyebrow {
  display: block;
  color: var(--magenta);
  font-size: 13.1px;
  font-weight: 600;
  line-height: 24.8px;
  letter-spacing: 2.2px;
}

.auth-copy-title {
  margin: 11.6px 0 0;
  color: var(--navy);
  font-family: 'Inter Tight', 'Inter', sans-serif;
  font-size: 29.7px;
  font-weight: 600;
  line-height: 34px;
  letter-spacing: -0.01em;
}

.auth-copy-title-sm {
  margin-top: 10.4px;
  font-size: 26.1px;
  line-height: 30.7px;
}

.auth-copy-text {
  margin: 14.3px 0 0;
  color: var(--text-muted);
  font-size: 14px;
  line-height: 22.8px;
}

.auth-checklist {
  list-style: none;
  margin: 8.6px 0 0;
  padding: 0;
}

.auth-checklist li {
  position: relative;
  padding-left: 20.2px;
  margin-bottom: 12.6px;
  color: var(--text-check);
  font-size: 13.1px;
  font-weight: 500;
  line-height: 15.2px;
}

.auth-checklist li i {
  position: absolute;
  left: 1.9px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--magenta);
  font-size: 8.8px;
  line-height: 1;
}

/* Elementos decorativos */
.auth-decor {
  height: 342.1px;
  width: auto;
  flex-shrink: 0;
  transform: translateY(6.9px);
  pointer-events: none;
  user-select: none;
}

.auth-decor-left {
  margin-right: 5.6px;
}

.auth-decor-right {
  margin-left: -4.5px;
}

/* Card */
.auth-card-wrap {
  position: relative;
  z-index: 1;
  flex-shrink: 0;
}

.auth-card {
  width: 371.3px;
  flex-shrink: 0;
  padding: 46.8px 35.8px 53.4px 34.6px;
  background: #fff;
  border: 1px solid #d3daea;
  border-radius: 17.6px;
  box-shadow: 0 7.6px 25.2px rgba(3, 29, 125, 0.05);
}

.auth-card-title {
  margin: 0 0 0 -1.3px;
  color: var(--navy);
  font-family: 'Inter Tight', 'Inter', sans-serif;
  font-size: 33.8px;
  font-weight: 600;
  line-height: 41.4px;
  letter-spacing: -0.016em;
  white-space: nowrap;
}

.auth-card-subtitle {
  margin: 9.2px 0 0 -1.3px;
  color: var(--text-muted);
  font-size: 14.6px;
  line-height: 24px;
}

.auth-label {
  display: block;
  margin: 20.7px 0 5.9px;
  color: var(--text-label);
  font-size: 13.1px;
  font-weight: 600;
  line-height: 19.8px;
}

.auth-label-password {
  margin-top: 13.1px;
}

.auth-input {
  display: block;
  width: 100%;
  height: 42.8px;
  padding: 0 13.2px 3.8px 11.1px;
  background: #fff;
  border: 1px solid var(--input-border);
  border-radius: 5.7px;
  color: #1f2333;
  font-family: inherit;
  font-size: 14.9px;
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.auth-input::placeholder {
  color: var(--text-muted);
  opacity: 1;
}

/* App.vue pinta de verde todo input:required:valid; en el login se anula */
.auth-input:required:valid {
  border: 1px solid var(--input-border);
}

.auth-input:focus,
.auth-input:required:valid:focus {
  border-color: var(--navy);
  box-shadow: 0 0 0 1.9px rgba(3, 29, 125, 0.1);
}

.auth-input.is-invalid,
.auth-input.is-invalid:required:valid {
  border-color: #dc3545;
}

.auth-feedback {
  margin-top: 3.8px;
  color: #dc3545;
  font-size: 11.7px;
}

.auth-password {
  position: relative;
}

.auth-password .auth-input {
  padding-right: 37.8px;
}

.auth-password-toggle {
  position: absolute;
  top: 50%;
  right: 14.5px;
  transform: translateY(-50%);
  padding: 0;
  background: none;
  border: none;
  color: #60677c;
  font-size: 13.2px;
  line-height: 1;
  cursor: pointer;
}

.auth-password-toggle:hover {
  color: var(--navy);
}

.auth-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 18.8px;
}

.auth-remember {
  display: flex;
  align-items: center;
  margin: 0;
  color: #878787;
  font-size: 13.5px;
  line-height: 20.8px;
  cursor: pointer;
}

.auth-remember input {
  -webkit-appearance: none;
  appearance: none;
  width: 13.2px;
  height: 13.2px;
  flex-shrink: 0;
  margin: 0 4.5px 0 0;
  background: #fff;
  border: 1px solid var(--input-border);
  border-radius: 2.5px;
  background: #fff url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath fill='none' stroke='%23fff' stroke-width='2.2' stroke-linecap='round' stroke-linejoin='round' d='M3.5 8.5l3 3 6-7'/%3E%3C/svg%3E") center / 0 no-repeat;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease, background-size 0.2s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.auth-remember input:checked {
  background-color: var(--navy);
  background-size: 8.8px;
  border-color: var(--navy);
}

.auth-remember input:focus-visible {
  outline: 1.8px solid rgba(3, 29, 125, 0.35);
  outline-offset: 1.8px;
}

.auth-forgot {
  margin-right: 13.9px;
  color: var(--navy);
  font-size: 11.7px;
  font-weight: 500;
  line-height: 20.8px;
  text-decoration: none;
}

.auth-forgot:hover {
  color: var(--navy);
  text-decoration: underline;
}

.auth-submit {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 18.3px;
  width: 100%;
  height: 41px;
  margin-top: 24.6px;
  padding: 0 0 1.8px;
  background: var(--navy-button);
  border: none;
  /* App.vue fuerza 12px en todos los botones con !important */
  border-radius: 5.7px !important;
  color: #fff;
  font-family: inherit;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.auth-submit i {
  font-size: 11.3px;
  line-height: 1;
  opacity: 0.85;
  transition: translate 0.2s ease;
}

.auth-submit:hover:not(:disabled) i {
  translate: 4px 0;
}

.auth-spinner {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: #fff;
  border-radius: 50%;
  animation: auth-spin 0.7s linear infinite;
}

.auth-submit:hover:not(:disabled) {
  background: #00145a;
}

.auth-submit:disabled {
  cursor: not-allowed;
}

.auth-signup {
  display: flex;
  justify-content: center;
  margin: 37.4px 0 0 -6.3px;
  color: var(--text-muted);
  font-size: 13.1px;
  line-height: 19.8px;
}

.auth-signup-link {
  margin-left: 17px;
  color: var(--magenta);
  font-weight: 500;
  text-decoration: none;
}

.auth-signup-link:hover {
  color: var(--magenta);
  text-decoration: underline;
}

.auth-alert {
  margin: 12.6px 0 0;
  font-size: 12.6px;
}

.auth-alert-error {
  margin-top: 10.1px;
}

/* Animaciones */
.auth-card-wrap {
  animation: auth-rise 0.6s cubic-bezier(0.22, 1, 0.36, 1) both;
}

.auth-decor-left {
  animation: auth-from-left 0.75s cubic-bezier(0.22, 1, 0.36, 1) 0.1s both;
}

.auth-decor-right {
  animation: auth-from-right 0.75s cubic-bezier(0.22, 1, 0.36, 1) 0.1s both;
}

.auth-copy > *,
.auth-checklist li {
  animation: auth-fade-up 0.5s cubic-bezier(0.22, 1, 0.36, 1) both;
}

.auth-copy > :nth-child(1) { animation-delay: 0.3s; }
.auth-copy > :nth-child(2) { animation-delay: 0.38s; }
.auth-copy > :nth-child(3) { animation-delay: 0.46s; }
.auth-checklist li:nth-child(1) { animation-delay: 0.52s; }
.auth-checklist li:nth-child(2) { animation-delay: 0.58s; }
.auth-checklist li:nth-child(3) { animation-delay: 0.64s; }

.auth-card--shake {
  animation: auth-shake 0.42s cubic-bezier(0.36, 0.07, 0.19, 0.97);
}

@keyframes auth-rise {
  from { opacity: 0; translate: 0 14px; }
  to { opacity: 1; translate: 0 0; }
}

@keyframes auth-from-left {
  from { opacity: 0; translate: -48px 0; }
  to { opacity: 1; translate: 0 0; }
}

@keyframes auth-from-right {
  from { opacity: 0; translate: 48px 0; }
  to { opacity: 1; translate: 0 0; }
}

@keyframes auth-fade-up {
  from { opacity: 0; translate: 0 10px; }
  to { opacity: 1; translate: 0 0; }
}

@keyframes auth-shake {
  0%, 100% { translate: 0 0; }
  20% { translate: -8px 0; }
  40% { translate: 7px 0; }
  60% { translate: -5px 0; }
  80% { translate: 3px 0; }
}

@keyframes auth-spin {
  to { transform: rotate(360deg); }
}

@media (prefers-reduced-motion: reduce) {
  .auth-card-wrap,
  .auth-decor,
  .auth-copy > *,
  .auth-checklist li,
  .auth-card--shake {
    animation: none;
  }

  .auth-submit i,
  .auth-remember input,
  .auth-input {
    transition: none;
  }

  .auth-submit:hover:not(:disabled) i {
    translate: none;
  }
}

/* Footer */
.auth-footer {
  height: 53.6px;
  flex-shrink: 0;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 14.6px 48.2px 0 50.4px;
  color: var(--text-muted);
  font-size: 13.1px;
  line-height: 19.8px;
}

.auth-footer-links {
  display: flex;
  gap: 56.1px;
}

.auth-footer-links a {
  color: var(--text-muted);
  text-decoration: none;
  white-space: nowrap;
}

.auth-footer-links a:hover {
  color: var(--navy);
}

/* Responsive */
@media (max-width: 1229.98px) {
  .auth-copy {
    display: none;
  }
}

@media (max-width: 849.98px) {
  .auth-decor {
    display: none;
  }

  .auth-shell {
    margin-left: 21.6px;
    margin-right: 21.6px;
  }

  .auth-header {
    padding: 0 7.2px 9.5px;
  }

  .auth-footer {
    padding: 14.6px 7.2px 0;
    gap: 16px;
  }

  .auth-footer-links {
    gap: 24px;
  }
}

@media (max-width: 640px) {
  .auth-shell {
    margin-left: 0;
    margin-right: 0;
  }

  .auth-header {
    height: auto;
    padding: 10.8px 14.4px;
  }

  .auth-logo {
    width: 166px;
    height: 58.8px;
  }

  .auth-logo img {
    height: 114.9px;
    margin: -30.2px 0 0 -5.4px;
  }

  .auth-help span,
  .auth-divider {
    display: none;
  }

  .auth-locale {
    margin-left: 14.4px;
  }

  .auth-stage {
    padding: 21.6px 14.4px;
  }

  .auth-card-wrap {
    width: 100%;
  }

  .auth-card {
    width: 100%;
    padding: 28.8px 18px;
  }

  .auth-card-title {
    font-size: 28.8px;
    line-height: 36px;
    white-space: normal;
  }

  .auth-row {
    flex-wrap: wrap;
    gap: 10.8px;
  }

  .auth-forgot {
    margin-right: 0;
  }

  .auth-footer {
    height: auto;
    flex-direction: column;
    align-items: center;
    gap: 7.2px;
    padding: 14.4px;
    text-align: center;
  }

  .auth-footer-links {
    gap: 21.6px;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/SignIn.vue